// Kroky tahu (P6a, P9): živě během odpovědi, po dokončení sbalené nad zprávou do řádku „2 kroky“.
import { useState } from 'react';
import { CaretRightIcon, CheckIcon, ProhibitIcon, WarningIcon } from '@phosphor-icons/react';
import { Badge, Spinner } from '@smworks-cz/ui-kit';
import { toolLabel, type Step } from '@sam/shared';
import { fmtMs, fmtParams, fmtTime, plural, reasonText } from '../lib/format';

function StepIcon({ status }: { status: Step['status'] }) {
  if (status === 'running') return <Spinner size={14} />;
  if (status === 'done') return <CheckIcon size={14} weight="bold" className="ok" />;
  if (status === 'denied') return <ProhibitIcon size={14} weight="bold" className="bad" />;
  return <WarningIcon size={14} weight="bold" className="warn" />;
}

/** Živé kroky během tahu. */
export function LiveSteps({ steps }: { steps: Step[] }) {
  if (!steps.length) return null;
  return (
    <ul className="steps-live" aria-live="polite">
      {steps.map((s) => {
        const l = toolLabel(s.tool);
        return (
          <li key={s.id} className={`step-live is-${s.status}`}>
            <StepIcon status={s.status} />
            <span>{s.status === 'running' ? `${l.running}…` : l.done}</span>
          </li>
        );
      })}
    </ul>
  );
}

/** Sbalené kroky dokončené odpovědi s rozkliknutelným detailem. */
export function StepsSummary({ steps }: { steps: Step[] }) {
  const [open, setOpen] = useState(false);
  if (!steps.length) return null;
  const denied = steps.filter((s) => s.status === 'denied').length;
  const failed = steps.filter((s) => s.status === 'error').length;
  return (
    <div className={`steps ${open ? 'open' : ''}`}>
      <button type="button" className="steps-toggle" onClick={() => setOpen(!open)} aria-expanded={open}>
        <CaretRightIcon size={12} weight="bold" className="caret" />
        <span>
          {steps.length} {plural(steps.length, 'krok', 'kroky', 'kroků')}
        </span>
        {denied > 0 && <Badge size="sm" variant="danger" label={`${denied} ${plural(denied, 'zamítnut', 'zamítnuty', 'zamítnuto')}`} />}
        {failed > 0 && <Badge size="sm" variant="warning" label={`${failed} ${plural(failed, 'chyba', 'chyby', 'chyb')}`} />}
      </button>
      {open && (
        <div className="steps-detail">
          {steps.map((s) => (
            <StepDetail key={s.id} step={s} />
          ))}
        </div>
      )}
    </div>
  );
}

function StepDetail({ step }: { step: Step }) {
  const l = toolLabel(step.tool);
  const d = step.detail;
  return (
    <div className={`step-card is-${step.status}`}>
      <div className="step-card-h">
        <StepIcon status={step.status} />
        <b>{l.done}</b>
        <code>{step.tool}</code>
        <span className="grow" />
        {d && <Badge size="sm" variant={d.decision === 'allow' ? 'success' : 'danger'} label={d.decision === 'allow' ? 'povoleno' : 'zamítnuto'} />}
      </div>
      {d ? (
        <dl className="step-dl">
          <dt>Zdroj</dt>
          <dd>{d.source ?? l.source}</dd>
          <dt>Parametry</dt>
          <dd className="mono">{fmtParams(d.params)}</dd>
          {d.decision === 'allow' ? (
            <>
              <dt>Řádků</dt>
              <dd>{d.rows ?? '–'}</dd>
            </>
          ) : (
            <>
              <dt>Důvod</dt>
              <dd className="bad">{reasonText(d.reason)}</dd>
            </>
          )}
          {d.decision === 'allow' && d.reason && (
            <>
              <dt>Pozor</dt>
              <dd className="warn">{reasonText(d.reason)}</dd>
            </>
          )}
          <dt>Pravidlo</dt>
          <dd className="mono">{d.rule ?? '–'}</dd>
          <dt>Doba</dt>
          <dd>
            {fmtMs(d.ms)} · {fmtTime(d.ts)}
          </dd>
        </dl>
      ) : (
        <p className="muted small">Detail z auditu brány není k dispozici.</p>
      )}
    </div>
  );
}

/** Zamítnutí viditelné přímo v chatu (P10). */
export function DeniedNotice({ steps }: { steps: Step[] }) {
  const denied = steps.filter((s) => s.status === 'denied');
  if (!denied.length) return null;
  return (
    <div className="deny" role="note">
      <ProhibitIcon size={18} weight="bold" />
      <div>
        <b>Přístup zamítnut</b>
        {denied.map((s) => (
          <p key={s.id}>
            {toolLabel(s.tool).done}: {reasonText(s.detail?.reason) || 'bez oprávnění'}. Dotaz nebyl puštěn k datům, zamítnutí je v záznamu přístupů.
          </p>
        ))}
      </div>
    </div>
  );
}
